/**
 * CATÁLOGO DOCUMENTAL (FASE 4.4-E)
 *
 * Gestión mínima y usable del catálogo de procedimientos/instrucciones/
 * fichas técnicas/documentos de seguridad — crear el documento maestro,
 * subir versiones (PDF), publicarlas con su vigencia, retirarlas. Nunca
 * un gestor documental enterprise completo (encargo, sección 13): una
 * sola lista de tarjetas/acordeón, mismo criterio que no-conformidades.
 *
 * La asociación a un Plan vive en la pestaña «Documentos» del editor de
 * Plan (`editor-plan.tsx`), no aquí — este catálogo es transversal a
 * todos los planes.
 *
 * Pre-F14.2 (0095): «Visible para el cliente» ya no basta — hay que elegir
 * qué CLIENTES lo ven (ninguno por defecto). El selector sólo aparece con la
 * visibilidad activada; la autoridad es el backend (y la RLS).
 */
import { useId, useState, type ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';

import {
  api,
  descargarConSesion,
  ETIQUETAS_TIPO_DOCUMENTO,
  TIPOS_DOCUMENTO,
  type Cliente,
  type DocumentoMaestro,
  type TipoDocumento,
  type VersionDocumento,
} from '../api.ts';
import { Cargando, Error as ErrorCaja, fecha, fechaHora, useDatos, Vacio } from '../componentes/base.tsx';
import { Shell } from '../componentes/shell.tsx';
import { useSesion } from '../contexto.tsx';

function formatoBytes(n: number): string {
  if (n < 1024) return `${String(n)} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

async function abrirVersion(id: string): Promise<Blob | null> {
  return descargarConSesion(`/api/documentos/versiones/${id}/archivo`);
}

export function PaginaDocumentos(): ReactNode {
  const { puede } = useSesion();
  const puedeGestionar = puede('documentos:gestionar');
  const [creando, setCreando] = useState(false);
  const [filtroEstado, setFiltroEstado] = useState<'' | 'ACTIVO' | 'RETIRADO'>('ACTIVO');

  const estado = useDatos(
    () => api.get<{ filas: DocumentoMaestro[] }>(`/api/documentos${filtroEstado ? `?estado=${filtroEstado}` : ''}`),
    [filtroEstado],
  );

  return (
    <Shell titulo="Documentos" subtitulo="Catálogo de procedimientos, instrucciones, fichas técnicas y documentos de seguridad">
      <div className="panel">
        <div className="panel-cabecera">
          <h2>Catálogo</h2>
          <div className="fila" style={{ gap: 10 }}>
            <select value={filtroEstado} onChange={(e) => setFiltroEstado(e.target.value as typeof filtroEstado)} aria-label="Filtrar por estado">
              <option value="ACTIVO">Activos</option>
              <option value="RETIRADO">Retirados</option>
              <option value="">Todos</option>
            </select>
            {puedeGestionar && !creando && (
              <button type="button" className="pequeno" onClick={() => setCreando(true)}>
                Nuevo documento
              </button>
            )}
          </div>
        </div>
        <div className="panel-cuerpo">
          {creando && (
            <FormularioCrearDocumento
              cancelar={() => setCreando(false)}
              alCrear={() => {
                setCreando(false);
                estado.recargar();
              }}
            />
          )}
          {estado.cargando && estado.datos === null && <Cargando texto="Cargando catálogo…" />}
          {estado.error !== null && <ErrorCaja error={estado.error} reintentar={estado.recargar} />}
          {estado.datos !== null &&
            (estado.datos.filas.length === 0 ? (
              <Vacio titulo="No hay documentos en el catálogo" />
            ) : (
              <ul className="lista-nc">
                {estado.datos.filas.map((f) => (
                  <TarjetaDocumento key={f.id} resumen={f} puedeGestionar={puedeGestionar} onCambiado={estado.recargar} />
                ))}
              </ul>
            ))}
        </div>
      </div>
    </Shell>
  );
}

/**
 * Clientes autorizados: casillas, una por cliente (con buscador si hay muchos).
 * Nunca «todos» de golpe: se marca cada cliente que de verdad debe verlo.
 */
export function SelectorClientesAutorizados({
  clientes, elegidos, cambiar,
}: { clientes: readonly Cliente[]; elegidos: ReadonlySet<string>; cambiar: (s: ReadonlySet<string>) => void }): ReactNode {
  const idBase = useId();
  const [filtro, setFiltro] = useState('');
  const visibles = clientes.filter((c) => filtro.trim() === '' || c.razonSocial.toLowerCase().includes(filtro.trim().toLowerCase()));
  function alternar(id: string): void {
    const s = new Set(elegidos);
    if (s.has(id)) s.delete(id); else s.add(id);
    cambiar(s);
  }
  return (
    <fieldset className="selector-clientes" style={{ border: 0, padding: 0, margin: '8px 0 12px' }}>
      <legend className="etiqueta-dato">Clientes autorizados</legend>
      {clientes.length > 8 && (
        <input
          type="search" aria-label="Buscar cliente" placeholder="Buscar cliente…" value={filtro}
          onChange={(e) => setFiltro(e.target.value)} id={`${idBase}-buscar`} style={{ marginBottom: 6 }}
        />
      )}
      <ul className="lista-casillas" aria-label="Clientes autorizados">
        {visibles.map((c) => (
          <li key={c.id}>
            <label className="fila" style={{ gap: 6 }}>
              <input type="checkbox" checked={elegidos.has(c.id)} onChange={() => alternar(c.id)} />
              {c.razonSocial}
            </label>
          </li>
        ))}
      </ul>
      <p className="pequena tenue" style={{ margin: 0 }}>
        {elegidos.size === 0 ? 'Ningún cliente lo verá hasta que marques al menos uno.' : `Lo verán ${String(elegidos.size)} cliente(s).`}
      </p>
    </fieldset>
  );
}

function useClientes(activo: boolean): Cliente[] {
  const estado = useDatos(
    () => (activo ? api.get<{ clientes: Cliente[] }>('/api/clientes') : Promise.resolve({ clientes: [] as Cliente[] })),
    [activo],
  );
  return estado.datos?.clientes ?? [];
}

function FormularioCrearDocumento({ cancelar, alCrear }: { cancelar: () => void; alCrear: () => void }): ReactNode {
  const idBase = useId();
  //  Nacido en el contexto de un cliente (`?idCliente=`): ese cliente viene ya marcado; el backend lo valida.
  const [parametros] = useSearchParams();
  const clienteDeContexto = parametros.get('idCliente');
  const clientes = useClientes(true);
  const [elegidos, setElegidos] = useState<ReadonlySet<string> | null>(null);
  //  Un usuario que sólo ve un cliente (ámbito de un cliente) lo tiene preseleccionado: no hay otro posible.
  const preseleccion = clienteDeContexto !== null && clientes.some((c) => c.id === clienteDeContexto)
    ? new Set([clienteDeContexto])
    : clientes.length === 1 ? new Set([clientes[0]!.id]) : new Set<string>();
  const clientesElegidos = elegidos ?? preseleccion;
  const [tipoDocumento, setTipoDocumento] = useState<TipoDocumento>('PROCEDIMIENTO');
  const [codigo, setCodigo] = useState('');
  const [titulo, setTitulo] = useState('');
  const [descripcion, setDescripcion] = useState('');
  const [visibleCliente, setVisibleCliente] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState<unknown>(null);

  async function enviar(): Promise<void> {
    if (codigo.trim() === '' || titulo.trim() === '') {
      setError(new globalThis.Error('Código y título son obligatorios.'));
      return;
    }
    setEnviando(true);
    setError(null);
    try {
      await api.post('/api/documentos', {
        tipoDocumento, codigo: codigo.trim(), titulo: titulo.trim(),
        descripcion: descripcion.trim() || null, visibleCliente,
        //  Sin visibilidad no se mandan clientes: no hay nada que autorizar.
        idsClientes: visibleCliente ? [...clientesElegidos] : [],
      });
      alCrear();
    } catch (e) {
      setError(e);
    } finally {
      setEnviando(false);
    }
  }

  return (
    <div className="panel accion-linea">
      <div className="panel-cabecera">
        <h3>Nuevo documento</h3>
        <button type="button" className="pequeno" onClick={cancelar} disabled={enviando}>Cancelar</button>
      </div>
      <div className="panel-cuerpo">
        {error !== null && <ErrorCaja error={error} />}
        <div className="fila" style={{ gap: 12, flexWrap: 'wrap' }}>
          <div className="campo" style={{ margin: 0 }}>
            <label htmlFor={`${idBase}-tipo`}>Tipo</label>
            <select id={`${idBase}-tipo`} value={tipoDocumento} onChange={(e) => setTipoDocumento(e.target.value as TipoDocumento)}>
              {TIPOS_DOCUMENTO.map((t) => (
                <option key={t} value={t}>{ETIQUETAS_TIPO_DOCUMENTO[t]}</option>
              ))}
            </select>
          </div>
          <div className="campo" style={{ margin: 0 }}>
            <label htmlFor={`${idBase}-codigo`}>Código</label>
            <input id={`${idBase}-codigo`} value={codigo} onChange={(e) => setCodigo(e.target.value)} />
          </div>
        </div>
        <div className="campo">
          <label htmlFor={`${idBase}-titulo`}>Título</label>
          <input id={`${idBase}-titulo`} value={titulo} onChange={(e) => setTitulo(e.target.value)} />
        </div>
        <div className="campo">
          <label htmlFor={`${idBase}-desc`}>Descripción (opcional)</label>
          <textarea id={`${idBase}-desc`} value={descripcion} onChange={(e) => setDescripcion(e.target.value)} rows={2} />
        </div>
        <label className="fila" style={{ gap: 6 }}>
          <input type="checkbox" checked={visibleCliente} onChange={(e) => setVisibleCliente(e.target.checked)} />
          Visible para cliente (portal)
        </label>
        {visibleCliente && <SelectorClientesAutorizados clientes={clientes} elegidos={clientesElegidos} cambiar={setElegidos} />}
        <button type="button" className="principal" disabled={enviando} onClick={() => void enviar()}>
          {enviando ? 'Creando…' : 'Crear documento'}
        </button>
      </div>
    </div>
  );
}

function TarjetaDocumento({
  resumen, puedeGestionar, onCambiado,
}: { resumen: DocumentoMaestro; puedeGestionar: boolean; onCambiado: () => void }): ReactNode {
  const [abierta, setAbierta] = useState(false);

  return (
    <li className="nc-tarjeta">
      <button type="button" className="nc-tarjeta-cabecera" aria-expanded={abierta} onClick={() => setAbierta((v) => !v)}>
        <span className="fila" style={{ gap: 8, flexWrap: 'wrap' }}>
          <span className="mono">{resumen.codigo}</span>
          <span className="etiqueta info">{ETIQUETAS_TIPO_DOCUMENTO[resumen.tipoDocumento] ?? resumen.tipoDocumento}</span>
          <span className={`etiqueta ${resumen.estado === 'ACTIVO' ? 'exito' : 'error'}`}>
            {resumen.estado === 'ACTIVO' ? 'Activo' : 'Retirado'}
          </span>
          {resumen.visibleCliente && <span className="etiqueta neutra">Visible al cliente</span>}
          {resumen.visibleCliente && resumen.clientesAutorizados !== undefined && resumen.clientesAutorizados.length === 0 && (
            <span className="etiqueta aviso">Sin clientes autorizados</span>
          )}
        </span>
        <span className="pequena suave">{resumen.titulo}</span>
      </button>
      {abierta && <DetalleDocumento id={resumen.id} maestro={resumen} puedeGestionar={puedeGestionar} onCambiado={onCambiado} />}
    </li>
  );
}

function DetalleDocumento({
  id, maestro, puedeGestionar, onCambiado,
}: { id: string; maestro: DocumentoMaestro; puedeGestionar: boolean; onCambiado: () => void }): ReactNode {
  const [subiendo, setSubiendo] = useState(false);
  const estado = useDatos(() => api.get<{ filas: VersionDocumento[] }>(`/api/documentos/${id}/versiones`), [id]);

  function alCambiar(): void {
    estado.recargar();
    onCambiado();
  }

  async function retirarMaestro(): Promise<void> {
    await api.post(`/api/documentos/${id}/retirar`, {});
    onCambiado();
  }

  return (
    <div className="nc-detalle">
      {maestro.descripcion !== null && maestro.descripcion !== '' && <p className="pequena">{maestro.descripcion}</p>}
      {maestro.clientesAutorizados !== undefined && (
        <VisibilidadDelDocumento maestro={maestro} puedeGestionar={puedeGestionar && maestro.estado === 'ACTIVO'} onCambiado={onCambiado} />
      )}
      <div className="sep">
        <div className="fila" style={{ justifyContent: 'space-between' }}>
          <div className="etiqueta-dato">Versiones</div>
          {puedeGestionar && maestro.estado === 'ACTIVO' && !subiendo && (
            <div className="fila" style={{ gap: 8 }}>
              <button type="button" className="pequeno" onClick={() => setSubiendo(true)}>Subir nueva versión</button>
              <button type="button" className="pequeno peligro" onClick={() => void retirarMaestro()}>Retirar documento</button>
            </div>
          )}
        </div>
        {subiendo && (
          <FormularioSubirVersion idDocumento={id} cancelar={() => setSubiendo(false)} alSubir={() => { setSubiendo(false); alCambiar(); }} />
        )}
        {estado.cargando && estado.datos === null && <Cargando texto="Cargando versiones…" />}
        {estado.error !== null && <ErrorCaja error={estado.error} reintentar={estado.recargar} />}
        {estado.datos !== null &&
          (estado.datos.filas.length === 0 ? (
            <Vacio titulo="Todavía no hay ninguna versión" />
          ) : (
            <ul className="lista-nc-historial">
              {estado.datos.filas.map((v) => (
                <TarjetaVersion key={v.id} version={v} puedeGestionar={puedeGestionar} onCambiado={alCambiar} />
              ))}
            </ul>
          ))}
      </div>
    </div>
  );
}

/**
 * Quién puede ver el documento y (gestión) cambiarlo DESPUÉS de crearlo: activar o retirar «Visible para
 * cliente» y elegir los clientes autorizados. Surte efecto en el acto en catálogo, versiones y descarga.
 */
function VisibilidadDelDocumento({
  maestro, puedeGestionar, onCambiado,
}: { maestro: DocumentoMaestro; puedeGestionar: boolean; onCambiado: () => void }): ReactNode {
  const actuales = maestro.clientesAutorizados ?? [];
  const [editando, setEditando] = useState(false);
  const clientes = useClientes(editando);
  const [visible, setVisible] = useState(maestro.visibleCliente);
  const [elegidos, setElegidos] = useState<ReadonlySet<string>>(new Set(actuales.map((c) => c.id)));
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState<unknown>(null);

  function empezar(): void {
    setVisible(maestro.visibleCliente);
    setElegidos(new Set(actuales.map((c) => c.id)));
    setError(null);
    setEditando(true);
  }

  async function guardar(): Promise<void> {
    setEnviando(true);
    setError(null);
    try {
      await api.put(`/api/documentos/${maestro.id}/visibilidad`, { visibleCliente: visible, idsClientes: visible ? [...elegidos] : [] });
      setEditando(false);
      onCambiado();
    } catch (e) {
      setError(e);
    } finally {
      setEnviando(false);
    }
  }

  return (
    <div className="sep">
      <div className="fila" style={{ justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
        <div>
          <div className="etiqueta-dato">Visibilidad para clientes</div>
          <div className="pequena">
            {!maestro.visibleCliente
              ? 'Solo interno: ningún cliente lo ve.'
              : actuales.length === 0
                ? 'Visible para cliente, pero sin clientes autorizados: ningún cliente lo ve todavía.'
                : `Lo ven: ${actuales.map((c) => c.razonSocial).join(', ')}.`}
          </div>
        </div>
        {puedeGestionar && !editando && (
          <button type="button" className="pequeno" onClick={empezar}>Cambiar visibilidad</button>
        )}
      </div>
      {editando && (
        <div className="panel accion-linea" style={{ marginTop: 8 }}>
          <div className="panel-cuerpo">
            {error !== null && <ErrorCaja error={error} />}
            <label className="fila" style={{ gap: 6 }}>
              <input type="checkbox" checked={visible} onChange={(e) => setVisible(e.target.checked)} />
              Visible para cliente (portal)
            </label>
            {visible && <SelectorClientesAutorizados clientes={clientes} elegidos={elegidos} cambiar={setElegidos} />}
            <div className="fila" style={{ gap: 8 }}>
              <button type="button" className="principal" disabled={enviando} onClick={() => void guardar()}>
                {enviando ? 'Guardando…' : 'Guardar visibilidad'}
              </button>
              <button type="button" className="pequeno" disabled={enviando} onClick={() => setEditando(false)}>Cancelar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function FormularioSubirVersion({ idDocumento, cancelar, alSubir }: { idDocumento: string; cancelar: () => void; alSubir: () => void }): ReactNode {
  const idBase = useId();
  const [archivo, setArchivo] = useState<File | null>(null);
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState<unknown>(null);

  async function enviar(): Promise<void> {
    if (archivo === null) {
      setError(new globalThis.Error('Elige un PDF para adjuntar.'));
      return;
    }
    setEnviando(true);
    setError(null);
    try {
      const datos = new FormData();
      datos.set('archivo', archivo, archivo.name);
      await api.subir(`/api/documentos/${idDocumento}/versiones`, datos);
      alSubir();
    } catch (e) {
      setError(e);
    } finally {
      setEnviando(false);
    }
  }

  return (
    <div className="panel accion-linea" style={{ marginTop: 8 }}>
      <div className="panel-cabecera">
        <h4>Subir nueva versión</h4>
        <button type="button" className="pequeno" onClick={cancelar} disabled={enviando}>Cancelar</button>
      </div>
      <div className="panel-cuerpo">
        {error !== null && <ErrorCaja error={error} />}
        <div className="campo">
          <label htmlFor={`${idBase}-archivo`}>Fichero (PDF)</label>
          <input id={`${idBase}-archivo`} type="file" accept="application/pdf" onChange={(e) => setArchivo(e.target.files?.[0] ?? null)} />
        </div>
        <button type="button" className="principal" disabled={enviando} onClick={() => void enviar()}>
          {enviando ? 'Subiendo…' : 'Subir versión'}
        </button>
      </div>
    </div>
  );
}

function TarjetaVersion({ version, puedeGestionar, onCambiado }: { version: VersionDocumento; puedeGestionar: boolean; onCambiado: () => void }): ReactNode {
  const [publicando, setPublicando] = useState(false);
  const [cargandoArchivo, setCargandoArchivo] = useState(false);

  async function abrir(): Promise<void> {
    setCargandoArchivo(true);
    const blob = await abrirVersion(version.id);
    setCargandoArchivo(false);
    if (blob === null) return;
    const url = URL.createObjectURL(blob);
    window.open(url, '_blank', 'noopener');
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
  }

  async function retirar(): Promise<void> {
    await api.post(`/api/documentos/versiones/${version.id}/retirar`, {});
    onCambiado();
  }

  return (
    <li>
      <span className="pequena">
        v{version.version} · <span className={`etiqueta ${version.estado === 'PUBLICADA' ? 'exito' : version.estado === 'RETIRADA' ? 'error' : 'neutra'}`}>
          {version.estado === 'BORRADOR' ? 'Borrador' : version.estado === 'PUBLICADA' ? 'Publicada' : 'Retirada'}
        </span>
      </span>
      <span className="pequena tenue">
        {' '}· {version.nombreOriginal} · {formatoBytes(version.tamanoBytes)}
        {version.vigenteDesde !== null && ` · vigente desde ${fecha(version.vigenteDesde)}`}
        {version.vigenteHasta !== null && ` hasta ${fecha(version.vigenteHasta)}`}
        {' '}· creada {fechaHora(version.creadoEn)}
      </span>
      <div className="fila" style={{ gap: 8, marginTop: 4 }}>
        <button type="button" className="pequeno" disabled={cargandoArchivo} onClick={() => void abrir()}>
          {cargandoArchivo ? 'Abriendo…' : 'Abrir'}
        </button>
        {puedeGestionar && version.estado === 'BORRADOR' && !publicando && (
          <button type="button" className="pequeno" onClick={() => setPublicando(true)}>Publicar</button>
        )}
        {puedeGestionar && version.estado !== 'RETIRADA' && (
          <button type="button" className="pequeno peligro" onClick={() => void retirar()}>Retirar</button>
        )}
      </div>
      {publicando && (
        <FormularioPublicarVersion idVersion={version.id} cancelar={() => setPublicando(false)} alPublicar={() => { setPublicando(false); onCambiado(); }} />
      )}
    </li>
  );
}

function FormularioPublicarVersion({ idVersion, cancelar, alPublicar }: { idVersion: string; cancelar: () => void; alPublicar: () => void }): ReactNode {
  const idBase = useId();
  const [vigenteDesde, setVigenteDesde] = useState('');
  const [vigenteHasta, setVigenteHasta] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState<unknown>(null);

  async function enviar(): Promise<void> {
    if (vigenteDesde.trim() === '') {
      setError(new globalThis.Error('La fecha de vigencia es obligatoria.'));
      return;
    }
    setEnviando(true);
    setError(null);
    try {
      await api.post(`/api/documentos/versiones/${idVersion}/publicar`, {
        vigenteDesde: vigenteDesde.trim(), vigenteHasta: vigenteHasta.trim() || null,
      });
      alPublicar();
    } catch (e) {
      setError(e);
    } finally {
      setEnviando(false);
    }
  }

  return (
    <div className="panel accion-linea" style={{ marginTop: 8 }}>
      <div className="panel-cabecera">
        <h4>Publicar versión</h4>
        <button type="button" className="pequeno" onClick={cancelar} disabled={enviando}>Cancelar</button>
      </div>
      <div className="panel-cuerpo">
        {error !== null && <ErrorCaja error={error} />}
        <div className="fila" style={{ gap: 12, flexWrap: 'wrap' }}>
          <div className="campo" style={{ margin: 0 }}>
            <label htmlFor={`${idBase}-desde`}>Vigente desde</label>
            <input id={`${idBase}-desde`} type="date" value={vigenteDesde} onChange={(e) => setVigenteDesde(e.target.value)} />
          </div>
          <div className="campo" style={{ margin: 0 }}>
            <label htmlFor={`${idBase}-hasta`}>Vigente hasta (opcional)</label>
            <input id={`${idBase}-hasta`} type="date" value={vigenteHasta} onChange={(e) => setVigenteHasta(e.target.value)} />
          </div>
        </div>
        <button type="button" className="principal" disabled={enviando} onClick={() => void enviar()}>
          {enviando ? 'Publicando…' : 'Publicar'}
        </button>
      </div>
    </div>
  );
}
